import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ChefHat } from "lucide-react";
import { api, nzd, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Badge, Loading } from "@/components/kit";

const EMPTY_FORM = {
  name: "", category: "Other", unit: "unit", is_ingredient: false,
  current_stock: 0, min_stock: 0, cost: 0, price: 0, supplier_id: "",
};

export default function Inventory() {
  const { activeStore } = useAuth();
  const [rows, setRows] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [editing, setEditing] = useState(null);      // product id for inline stock edit
  const [stockVal, setStockVal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formId, setFormId] = useState(null);        // null = create
  const [saving, setSaving] = useState(false);
  const [recipeFor, setRecipeFor] = useState(null);  // product being recipe-edited
  const [recipeItems, setRecipeItems] = useState([]);

  const load = useCallback(() => {
    if (!activeStore) return;
    setRows(null);
    api.get("/products", { params: { store_id: activeStore } }).then(({ data }) => setRows(data));
    api.get("/suppliers", { params: { store_id: activeStore } }).then(({ data }) => setSuppliers(data)).catch(() => {});
  }, [activeStore]);
  useEffect(load, [load]);

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "";

  // ---- product form ----
  const openCreate = (isIngredient) => {
    setForm({ ...EMPTY_FORM, is_ingredient: isIngredient });
    setFormId(null);
    setModalOpen(true);
  };
  const openEdit = (p) => {
    setForm({
      name: p.name, category: p.category, unit: p.unit, is_ingredient: p.is_ingredient,
      current_stock: p.current_stock ?? 0, min_stock: p.min_stock ?? 0,
      cost: p.cost ?? 0, price: p.price ?? 0, supplier_id: p.supplier_id || "",
    });
    setFormId(p.id);
    setModalOpen(true);
  };

  const saveForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, supplier_id: form.supplier_id || null };
      if (formId) await api.put(`/products/${formId}`, payload);
      else await api.post(`/products?store_id=${activeStore}`, payload);
      toast.success(formId ? "Item updated" : "Item added");
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save item");
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Items used in recipes will be deactivated instead.`)) return;
    try {
      const { data } = await api.delete(`/products/${p.id}`);
      if (data.deactivated) toast.info("Item is referenced by recipes/sales — marked inactive.");
      else toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const saveStock = async (id) => {
    try {
      await api.put(`/products/${id}`, { current_stock: Number(stockVal) });
      toast.success("Stock updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update stock");
    }
  };

  // ---- recipe editor ----
  const openRecipe = async (p) => {
    setRecipeFor(p);
    try {
      const { data } = await api.get(`/products/${p.id}/recipe`);
      setRecipeItems(data.length ? data.map((r) => ({ ingredient_id: r.ingredient_id, quantity: r.quantity })) : []);
    } catch {
      setRecipeItems([]);
    }
  };

  const saveRecipe = async () => {
    try {
      await api.put(`/products/${recipeFor.id}/recipe`, { items: recipeItems.filter((r) => r.ingredient_id && Number(r.quantity) > 0) });
      toast.success("Recipe saved");
      setRecipeFor(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save recipe");
    }
  };

  if (!rows) return <Loading />;
  const ingredients = rows.filter((r) => r.is_ingredient);
  const products = rows.filter((r) => !r.is_ingredient);

  return (
    <div>
      <PageHeader
        title="Inventory"
        subtitle={`${products.length} products · ${ingredients.length} ingredients · ${suppliers.length} suppliers`}
        right={
          <div className="flex gap-2">
            <button data-testid="add-product" onClick={() => openCreate(false)}
              className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 px-3 py-2 rounded-xl text-xs font-semibold shadow-2xs transition-colors">
              <Plus className="h-3.5 w-3.5" /> Menu product
            </button>
            <button data-testid="add-ingredient" onClick={() => openCreate(true)}
              className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-2 rounded-xl text-xs font-semibold transition-all shadow-md">
              <Plus className="h-3.5 w-3.5" /> Ingredient
            </button>
          </div>
        }
      />

      {/* Ingredients & stock */}
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-3">Ingredients & stock</div>
      <Card className="overflow-hidden mb-8" testid="ingredients-table">
        {ingredients.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            No ingredients yet. Add the raw materials you stock (flour, milk, beans…) so forecasts can compute purchase orders.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-[0.08em] text-zinc-400 border-b border-zinc-200">
              <th className="px-5 py-3 font-semibold">Item</th><th className="px-5 py-3 font-semibold">Unit</th>
              <th className="px-5 py-3 font-semibold text-right">Current</th><th className="px-5 py-3 font-semibold text-right">Min</th>
              <th className="px-5 py-3 font-semibold">Supplier</th><th className="px-5 py-3 font-semibold text-right">Cost</th>
              <th className="px-5 py-3 font-semibold text-center">Status</th><th className="px-5 py-3"></th>
            </tr></thead>
            <tbody>
              {ingredients.map((p) => (
                <tr key={p.id} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors duration-150">
                  <td className="px-5 py-3 font-medium text-zinc-900">{p.name}</td>
                  <td className="px-5 py-3 text-zinc-500">{p.unit}</td>
                  <td className="px-5 py-3 text-right tnum">
                    {editing === p.id ? (
                      <span className="inline-flex gap-1">
                        <input type="number" value={stockVal} onChange={(e) => setStockVal(e.target.value)} className="w-20 border border-zinc-200 rounded-md px-2 py-1 text-right" />
                        <button onClick={() => saveStock(p.id)} className="text-emerald-600 text-xs font-medium">Save</button>
                      </span>
                    ) : (
                      <button onClick={() => { setEditing(p.id); setStockVal(p.current_stock); }} className="hover:underline" title="Quick-edit stock">{num(p.current_stock, 1)}</button>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right tnum text-zinc-500">{num(p.min_stock, 1)}</td>
                  <td className="px-5 py-3 text-zinc-500">{supplierName(p.supplier_id) || p.supplier}</td>
                  <td className="px-5 py-3 text-right tnum text-zinc-600">{nzd(p.cost)}</td>
                  <td className="px-5 py-3 text-center">{p.current_stock < p.min_stock ? <Badge level="danger">Low</Badge> : <Badge level="success">OK</Badge>}</td>
                  <RowActions onEdit={() => openEdit(p)} onDelete={() => removeItem(p)} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Menu products */}
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-3">Menu products</div>
      <Card className="overflow-hidden mb-8" testid="products-table">
        {products.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            No menu products yet. Add what you sell (or import sales history in Settings and they'll be created automatically).
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-[0.08em] text-zinc-400 border-b border-zinc-200">
              <th className="px-5 py-3 font-semibold">Product</th><th className="px-5 py-3 font-semibold">Category</th>
              <th className="px-5 py-3 font-semibold text-right">Cost</th><th className="px-5 py-3 font-semibold text-right">Price</th>
              <th className="px-5 py-3 font-semibold text-right">Margin</th>
              <th className="px-5 py-3 font-semibold text-center">Recipe</th>
              <th className="px-5 py-3 font-semibold text-center">Active</th><th className="px-5 py-3"></th>
            </tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors duration-150">
                  <td className="px-5 py-3 font-medium text-zinc-900">{p.name}</td>
                  <td className="px-5 py-3 text-zinc-500">{p.category}</td>
                  <td className="px-5 py-3 text-right tnum text-zinc-600">{nzd(p.cost)}</td>
                  <td className="px-5 py-3 text-right tnum">{nzd(p.price)}</td>
                  <td className="px-5 py-3 text-right tnum text-emerald-600">{p.price ? Math.round((1 - p.cost / p.price) * 100) : 0}%</td>
                  <td className="px-5 py-3 text-center">
                    {!p.is_ingredient && (
                      <button onClick={() => openRecipe(p)} title="Edit recipe"
                        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 px-2 py-1 rounded-lg transition-colors">
                        <ChefHat className="h-3.5 w-3.5" /> Edit
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-3 text-center">{p.active ? <Badge level="success">Active</Badge> : <Badge>Off</Badge>}</td>
                  <RowActions onEdit={() => openEdit(p)} onDelete={() => removeItem(p)} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Suppliers */}
      <SupplierManager suppliers={suppliers} activeStore={activeStore} onChanged={load} />

      {/* Product create/edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onMouseDown={(e) => e.target === e.currentTarget && setModalOpen(false)}>
          <form onSubmit={saveForm} className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4" data-testid="product-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">{formId ? "Edit item" : form.is_ingredient ? "New ingredient" : "New menu product"}</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400"><X className="h-4 w-4" /></button>
            </div>

            <label className="block text-sm">
              <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Name</span>
              <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            </label>

            <div className="grid grid-cols-3 gap-3">
              <label className="block text-sm"><span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Category</span>
                <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Coffee / Food / …"
                  className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-900" /></label>
              <label className="block text-sm"><span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Unit</span>
                <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  placeholder="kg / L / unit"
                  className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-900" /></label>
              {form.is_ingredient && ["kg", "L"].includes(form.unit) && (
                <label className="flex items-end gap-2 pb-2 text-xs text-zinc-500">
                  <input type="checkbox" checked={form.is_ingredient} disabled /> kg/L quantities entered in grams/ml for recipes
                </label>
              )}
            </div>

            <div className="grid grid-cols-4 gap-3">
              <NumField label="Stock" v={form.current_stock} on={(v) => setForm({ ...form, current_stock: v })} />
              <NumField label="Min stock" v={form.min_stock} on={(v) => setForm({ ...form, min_stock: v })} />
              <NumField label={`Cost (${form.unit})`} v={form.cost} step="0.01" on={(v) => setForm({ ...form, cost: v })} />
              {!form.is_ingredient && <NumField label="Price" v={form.price} step="0.01" on={(v) => setForm({ ...form, price: v })} />}
            </div>

            <label className="block text-sm">
              <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Supplier</span>
              <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900">
                <option value="">— none —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setModalOpen(false)} className="border border-zinc-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-zinc-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                {saving ? "Saving…" : formId ? "Save changes" : "Add item"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Recipe editor modal */}
      {recipeFor && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onMouseDown={(e) => e.target === e.currentTarget && setRecipeFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4" data-testid="recipe-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Recipe · {recipeFor.name}</h3>
              <button onClick={() => setRecipeFor(null)} className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-zinc-500">How much of each ingredient one unit of this product uses.</p>

            {ingredients.length === 0 && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">Add ingredients first — recipes link menu items to stocked ingredients.</div>}

            {recipeItems.map((ri, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select value={ri.ingredient_id}
                  onChange={(e) => setRecipeItems(recipeItems.map((x, j) => j === i ? { ...x, ingredient_id: e.target.value } : x))}
                  className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">— choose ingredient —</option>
                  {ingredients.map((ing) => <option key={ing.id} value={ing.id}>{ing.name}</option>)}
                </select>
                <input type="number" min="0" step="any" value={ri.quantity}
                  onChange={(e) => setRecipeItems(recipeItems.map((x, j) => j === i ? { ...x, quantity: Number(e.target.value) } : x))}
                  className="w-24 border border-zinc-200 rounded-lg px-3 py-2 text-sm text-right" />
                <QtyUnitHint ingredient={ingredients.find((ing) => ing.id === ri.ingredient_id)} />
                <button onClick={() => setRecipeItems(recipeItems.filter((_, j) => j !== i))} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}

            {ingredients.length === 0 ? null : (
              <div className="text-[11px] text-zinc-400">
                Tip: for kg/L ingredients type grams/millilitres (180 = 180&nbsp;ml milk). Anything missing? Add it as an ingredient first, then link it here.
              </div>
            )}

            <button onClick={() => setRecipeItems([...recipeItems, { ingredient_id: "", quantity: 1 }])}
              className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-zinc-900 border border-dashed border-zinc-300 px-3 py-1.5 rounded-lg">
              <Plus className="h-3 w-3" /> Add ingredient line
            </button>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setRecipeFor(null)} className="border border-zinc-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-zinc-50">Cancel</button>
              <button onClick={saveRecipe} className="bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-medium">Save recipe</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QtyUnitHint({ ingredient }) {
  // Shows what unit the recipe quantity is typed in: g/ml for kg/L ingredients
  // (the engine stores base units), otherwise the ingredient's own unit.
  if (!ingredient) return <span className="text-xs text-zinc-300 w-8">—</span>;
  const label = ["kg", "L"].includes(ingredient.unit) ? "g/ml" : ingredient.unit;
  return (
    <span className="text-[11px] font-medium text-zinc-500 w-10 shrink-0" title={`Type the amount in ${label} — e.g. 180 for 180 ml of milk`}>
      {label}
    </span>
  );
}

function RowActions({ onEdit, onDelete }) {
  return (
    <td className="px-5 py-3 text-right whitespace-nowrap">
      <button onClick={onEdit} title="Edit" className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 mr-1"><Pencil className="h-3.5 w-3.5" /></button>
      <button onClick={onDelete} title="Delete" className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
    </td>
  );
}

function NumField({ label, v, on, step = "1" }) {
  return (
    <label className="block text-sm">
      <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{label}</span>
      <input type="number" step={step} min="0" value={v} onChange={(e) => on(e.target.value === "" ? "" : Number(e.target.value))}
        className="mt-1 w-full border border-zinc-200 rounded-lg px-3 py-2 text-right tnum focus:outline-none focus:ring-2 focus:ring-zinc-900" />
    </label>
  );
}

function SupplierManager({ suppliers, activeStore, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [lead, setLead] = useState(2);
  const [freq, setFreq] = useState("weekly");
  const [orderDay, setOrderDay] = useState(1);

  const add = async () => {
    try {
      await api.post(`/suppliers?store_id=${activeStore}`, { name, contact, lead_time_days: Number(lead), order_frequency: freq, order_day: Number(orderDay) });
      toast.success("Supplier added");
      setOpen(false); setName(""); setContact(""); setLead(2);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add supplier");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove supplier "${s.name}"?`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Supplier removed");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reassign its products first");
    }
  };

  return (
    <>
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-3 flex items-center justify-between">
        <span>Suppliers</span>
        <button data-testid="add-supplier" onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 px-3 py-2 rounded-xl text-xs font-semibold shadow-2xs transition-colors">
          <Plus className="h-3.5 w-3.5" /> Add supplier
        </button>
      </div>
      <Card className="overflow-hidden" testid="suppliers-table">
        {open && (
          <div className="p-4 border-b border-zinc-100 bg-zinc-50/60 flex flex-wrap gap-2 items-end">
            <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)}
              className="border border-zinc-200 rounded-lg px-3 py-2 text-sm w-44" />
            <input placeholder="Contact email" value={contact} onChange={(e) => setContact(e.target.value)}
              className="border border-zinc-200 rounded-lg px-3 py-2 text-sm w-56" />
            <label className="text-xs text-zinc-500 flex flex-col">
              Ordering cadence
              <select value={freq} onChange={(e) => setFreq(e.target.value)}
                className="border border-zinc-200 rounded-lg px-3 py-2 text-sm w-36 mt-1 bg-white">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
              </select>
            </label>
            <label className="text-xs text-zinc-500 flex flex-col">
              Lead days
              <input type="number" min="0" value={lead} onChange={(e) => setLead(e.target.value)}
                className="border border-zinc-200 rounded-lg px-3 py-2 text-sm w-24 mt-1" />
            </label>
            <label className="text-xs text-zinc-500 flex flex-col">
              Order day (weekly/biweekly)
              <select value={orderDay} onChange={(e) => setOrderDay(Number(e.target.value))}
                className="border border-zinc-200 rounded-lg px-3 py-2 text-sm w-36 mt-1 bg-white">
                <option value={0}>Monday</option>
                <option value={1}>Tuesday</option>
                <option value={2}>Wednesday</option>
                <option value={3}>Thursday</option>
                <option value={4}>Friday</option>
                <option value={5}>Saturday</option>
                <option value={6}>Sunday</option>
              </select>
            </label>
            <button onClick={add} disabled={!name.trim()}
              className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium">Save supplier</button>
          </div>
        )}
        {suppliers.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No suppliers yet. Purchasing recommendations use each ingredient's supplier and lead time.</div>
        ) : (
          <ul className="divide-y divide-zinc-50">
            {suppliers.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-5 py-3 text-sm">
                <div>
                  <span className="font-medium text-zinc-900">{s.name}</span>
                  <span className="ml-3 text-xs text-zinc-400">{s.contact || "no contact"} · lead {s.lead_time_days}d · {s.order_frequency || "daily"}</span>
                </div>
                <button onClick={() => remove(s)} className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
